// frontend/src/i18n/index.js
// =============================================================================
// hb_visits frontend internationalization bootstrap.
// =============================================================================
//
// English remains the source/default language. Polish is the first additional UI
// language. Backend/database enum values remain stable English technical codes;
// frontend dictionaries translate what users see.
//
// Public/auth language strategy:
// - before login, the initial display language is derived from browser/OS
//   language only; Polish browser locales start in PL, all other locales start
//   in EN;
// - LoginPage defaults to the separate "Profile language" mode, which applies
//   user.preferred_language only after successful authentication;
// - manual PL/EN selection on LoginPage is an in-memory current-session override;
// - no public login language mode is stored in localStorage/sessionStorage.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import plCommon from "./locales/pl/common.json";
import enNavigation from "./locales/en/navigation.json";
import plNavigation from "./locales/pl/navigation.json";
import enStock from "./locales/en/stock.json";
import plStock from "./locales/pl/stock.json";
import enServiceConsumption from "./locales/en/serviceConsumption.json";
import plServiceConsumption from "./locales/pl/serviceConsumption.json";
import enVouchers from "./locales/en/vouchers.json";
import plVouchers from "./locales/pl/vouchers.json";
import enVisits from "./locales/en/visits.json";
import plVisits from "./locales/pl/visits.json";
import enDashboard from "./locales/en/dashboard.json";
import plDashboard from "./locales/pl/dashboard.json";
import enMasterData from "./locales/en/masterData.json";
import plMasterData from "./locales/pl/masterData.json";
import enLeaves from "./locales/en/leaves.json";
import plLeaves from "./locales/pl/leaves.json";
import enSales from "./locales/en/sales.json";
import plSales from "./locales/pl/sales.json";
import enCatalog from "./locales/en/catalog.json";
import plCatalog from "./locales/pl/catalog.json";
import enReports from "./locales/en/reports.json";
import plReports from "./locales/pl/reports.json";
import enAdmin from "./locales/en/admin.json";
import plAdmin from "./locales/pl/admin.json";
import enUserSettings from "./locales/en/userSettings.json";
import plUserSettings from "./locales/pl/userSettings.json";
import enAuth from "./locales/en/auth.json";
import plAuth from "./locales/pl/auth.json";
import enSettings from "./locales/en/settings.json";
import plSettings from "./locales/pl/settings.json";
import enWorkItems from "./locales/en/workItems.json";
import plWorkItems from "./locales/pl/workItems.json";
import enShop from "./locales/en/shop.json";
import plShop from "./locales/pl/shop.json";
import enCommandSearch from "./locales/en/commandSearch.json";
import plCommandSearch from "./locales/pl/commandSearch.json";
import enSanitary from "./locales/en/sanitary.json";
import plSanitary from "./locales/pl/sanitary.json";

export const SUPPORTED_LANGUAGES = Object.freeze(["en", "pl"]);
export const DEFAULT_LANGUAGE = "en";

function resolveSupportedLanguage(value) {
  const raw = String(value || "").trim().toLowerCase().replace(/_/g, "-");
  if (!raw) return null;

  if (SUPPORTED_LANGUAGES.includes(raw)) {
    return raw;
  }

  const baseLanguage = raw.split("-")[0];
  return SUPPORTED_LANGUAGES.includes(baseLanguage) ? baseLanguage : null;
}

export function normalizeLanguage(value) {
  return resolveSupportedLanguage(value) || DEFAULT_LANGUAGE;
}

function readBrowserLanguage() {
  if (typeof navigator === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ];

  for (const candidate of candidates) {
    const resolved = resolveSupportedLanguage(candidate);
    if (resolved) return resolved;
  }

  return DEFAULT_LANGUAGE;
}

function readInitialPublicLanguage() {
  return readBrowserLanguage();
}

i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon, navigation: enNavigation, stock: enStock, serviceConsumption: enServiceConsumption, vouchers: enVouchers, visits: enVisits, dashboard: enDashboard, masterData: enMasterData, leaves: enLeaves, sales: enSales, catalog: enCatalog, reports: enReports, admin: enAdmin, userSettings: enUserSettings, auth: enAuth, settings: enSettings, workItems: enWorkItems, shop: enShop, commandSearch: enCommandSearch, sanitary: enSanitary },
    pl: { common: plCommon, navigation: plNavigation, stock: plStock, serviceConsumption: plServiceConsumption, vouchers: plVouchers, visits: plVisits, dashboard: plDashboard, masterData: plMasterData, leaves: plLeaves, sales: plSales, catalog: plCatalog, reports: plReports, admin: plAdmin, userSettings: plUserSettings, auth: plAuth, settings: plSettings, workItems: plWorkItems, shop: plShop, commandSearch: plCommandSearch, sanitary: plSanitary },
  },
  lng: readInitialPublicLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS: "common",
  ns: ["common", "navigation", "stock", "serviceConsumption", "vouchers", "visits", "dashboard", "masterData", "leaves", "sales", "catalog", "reports", "admin", "userSettings", "auth", "settings", "workItems", "shop", "commandSearch", "sanitary"],
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  returnEmptyString: false,
});

export default i18n;






